import {
  useState,
  useEffect,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  type FC
} from 'react';
import classnames from 'classnames';
import { shallowEqualArrays } from 'shallow-equal';

import type { SelectOption } from 'app/components/SelectDropdown';
import SelectDropdown from 'app/components/SelectDropdown';
import Tooltip from 'app/components/Tooltip';

import style from './style.scss';

interface Props<T extends number | string> {
  label?: ReactNode;
  selectionAsLabel?: boolean;
  clearLabel?: string;
  options: SelectOption<T>[];
  value: T[];
  multi?: boolean;
  onChange: (value: T[]) => void;
  loading?: boolean;
  error?: string;
  className?: string;
  portalClassName?: string;
  width?: number;
  itemClassName?: string;
  tooltipContent?: string;
  withSearch?: boolean;
}

const Wrapper: FC<{ tooltipContent?: string } & PropsWithChildren> = ({
  tooltipContent,
  children
}) => {
  if (tooltipContent) {
    return <Tooltip content={tooltipContent}>{children}</Tooltip>;
  }
  return <>{children}</>;
};

const FilterDropdown = <T extends number | string>({
  label,
  clearLabel,
  options,
  value,
  multi,
  onChange,
  loading,
  error,
  className,
  portalClassName,
  width,
  selectionAsLabel = false,
  itemClassName,
  tooltipContent,
  withSearch = false
}: Props<T>): ReactElement | null => {
  const [editingValue, setEditingValue] = useState(value);

  useEffect(() => {
    if (!shallowEqualArrays(value, editingValue)) {
      setEditingValue(value);
    }
  }, [value]);

  const handleChange = (value: T) => {
    const newValue = [value];
    setEditingValue(newValue);
    onChange(newValue);
  };

  const handleMultiChange = (value: T[]) => {
    setEditingValue(value);
  };

  const handleClose = () => {
    if (!shallowEqualArrays(value, editingValue)) {
      onChange(editingValue);
    }
  };

  const dropdownLabel =
    selectionAsLabel && !multi ? options.find(opt => opt.value === editingValue[0])?.label : label;

  return (
    <Wrapper tooltipContent={tooltipContent}>
      <SelectDropdown
        className={classnames(style.button, className)}
        variation="text"
        label={<>{dropdownLabel}</>}
        portalClassName={portalClassName}
        clearLabel={clearLabel}
        options={options}
        value={editingValue}
        multi={multi}
        loading={loading}
        error={error}
        onChange={handleChange}
        onMultiChange={handleMultiChange}
        onClose={handleClose}
        width={width}
        itemClassName={itemClassName}
        withSearch={withSearch}
      />
    </Wrapper>
  );
};

FilterDropdown.displayName = 'FilterDropdown';

export default FilterDropdown;
export type { SelectOption };
